import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PlantDialog } from "@/pages/Plants";
import { ProductDialog } from "@/pages/Products";
import { CreateEpdDialog } from "@/pages/Epds";
import { Check, Building2, Factory, Package, FileText } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import { toast } from "sonner";

const STEPS = [
  { key: "org", label: "Organisation", icon: Building2 },
  { key: "plant", label: "First Plant", icon: Factory },
  { key: "product", label: "First Product", icon: Package },
  { key: "epd", label: "Create EPD", icon: FileText },
];

const ORG_FIELDS = [
  ["legal_name", "Legal name *"], ["trading_name", "Trading name"], ["registration_number", "Registration number"],
  ["headquarters", "Headquarters"], ["website", "Website"], ["industry", "Industry"],
  ["sustainability_contact", "Sustainability contact"], ["epd_contact", "EPD contact"],
];

export default function Onboarding() {
  const nav = useNavigate();
  const { user, loadMe } = useAuth();
  const [step, setStep] = useState(0);
  const [org, setOrg] = useState({ country: "Nigeria" });
  const [plantOpen, setPlantOpen] = useState(false);
  const [productOpen, setProductOpen] = useState(false);
  const [epdOpen, setEpdOpen] = useState(false);
  const [hasPlant, setHasPlant] = useState(false);
  const [hasProduct, setHasProduct] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [o, p, pr] = await Promise.allSettled([
        api.get("/organization"), api.get("/plants"), api.get("/products"),
      ]);
      if (o.status === "fulfilled" && o.value?.data) setOrg((cur) => ({ country: "Nigeria", ...cur, ...o.value.data }));
      if (p.status === "fulfilled") setHasPlant(Array.isArray(p.value?.data) && p.value.data.length > 0);
      if (pr.status === "fulfilled") setHasProduct(Array.isArray(pr.value?.data) && pr.value.data.length > 0);
      setLoading(false);
    })();
  }, []);

  const set = (k, v) => setOrg((o) => ({ ...o, [k]: v }));

  const saveOrg = async () => {
    try { await api.put("/organization", org); setStep(1); }
    catch (e) { toast.error(apiError(e)); }
  };

  const finish = async () => {
    try {
      await api.post("/onboarding/organization", { organization: org, complete: true });
      await loadMe();
      nav("/app");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-[#0f172a] text-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-2.5">
          <BrandMark size={26} />
          <span className="font-display font-semibold">EPD Nigeria — Get started</span>
        </div>
      </div>
      {loading ? (
        <div className="max-w-3xl mx-auto p-10 text-center text-sm text-muted-foreground" data-testid="onboarding-loading">
          Loading your workspace…
        </div>
      ) : (
      <div className="max-w-3xl mx-auto p-6 md:p-10" data-testid="onboarding-wizard">
        <div className="flex items-center justify-between mb-8">
          {STEPS.map((s, i) => (
            <div key={s.key} className="flex items-center gap-2 flex-1">
              <div className={`h-9 w-9 rounded-full flex items-center justify-center border ${i < step ? "bg-emerald-600 text-white border-emerald-600" : i === step ? "bg-white border-emerald-600 text-emerald-700" : "bg-white border-slate-200 text-slate-400"}`}>
                {i < step ? <Check className="h-4 w-4" /> : <s.icon className="h-4 w-4" />}
              </div>
              <span className={`text-sm ${i === step ? "font-medium" : "text-slate-500"}`}>{s.label}</span>
              {i < STEPS.length - 1 && <div className="flex-1 h-px bg-slate-200 mx-2" />}
            </div>
          ))}
        </div>

        <div className="bg-white border rounded-lg p-6 md:p-8">
          {step === 0 && (
            <div>
              <h2 className="font-display text-xl font-semibold mb-1">Tell us about your organisation</h2>
              <p className="text-sm text-muted-foreground mb-5">This appears on your EPDs and public declaration records.</p>
              <div className="grid grid-cols-2 gap-4">
                {ORG_FIELDS.map(([k, label]) => (
                  <div key={k}>
                    <Label className="text-xs">{label}</Label>
                    <Input data-testid={`org-${k}`} value={org[k] || ""} onChange={(e) => set(k, e.target.value)} className="mt-1" />
                  </div>
                ))}
              </div>
              <div className="mt-6 flex justify-end">
                <Button data-testid="onboard-org-next" onClick={saveOrg} disabled={!org.legal_name}>Save &amp; continue</Button>
              </div>
            </div>
          )}
          {step === 1 && (
            <div className="text-center py-6">
              <Factory className="h-9 w-9 text-emerald-600 mx-auto mb-3" />
              <h2 className="font-display text-xl font-semibold">Add your first plant</h2>
              <p className="text-sm text-muted-foreground mt-1 mb-5 max-w-md mx-auto">Plant master data is reused across all products and EPDs.</p>
              {hasPlant && <p className="text-sm text-emerald-700 mb-3">✓ A plant has been added.</p>}
              <div className="flex gap-3 justify-center">
                <Button variant="outline" data-testid="onboard-add-plant" onClick={() => setPlantOpen(true)}>Add plant</Button>
                <Button data-testid="onboard-plant-next" onClick={() => setStep(2)} disabled={!hasPlant}>Continue</Button>
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="text-center py-6">
              <Package className="h-9 w-9 text-emerald-600 mx-auto mb-3" />
              <h2 className="font-display text-xl font-semibold">What product are you creating an EPD for?</h2>
              <p className="text-sm text-muted-foreground mt-1 mb-5">Choose Cement or Ready-Mix Concrete.</p>
              {hasProduct && <p className="text-sm text-emerald-700 mb-3">✓ A product has been added.</p>}
              <div className="flex gap-3 justify-center">
                <Button variant="outline" data-testid="onboard-add-product" onClick={() => setProductOpen(true)}>Add product</Button>
                <Button data-testid="onboard-product-next" onClick={() => setStep(3)} disabled={!hasProduct}>Continue</Button>
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="text-center py-6">
              <FileText className="h-9 w-9 text-emerald-600 mx-auto mb-3" />
              <h2 className="font-display text-xl font-semibold">Create your first EPD</h2>
              <p className="text-sm text-muted-foreground mt-1 mb-5">You're ready to begin the data journey.</p>
              <div className="flex gap-3 justify-center">
                <Button data-testid="onboard-create-epd" onClick={() => setEpdOpen(true)}>Create first EPD</Button>
                <Button variant="outline" data-testid="onboard-finish" onClick={finish}>Go to dashboard</Button>
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      <PlantDialog open={plantOpen} onOpenChange={setPlantOpen} onSaved={() => setHasPlant(true)} />
      <ProductDialog open={productOpen} onOpenChange={setProductOpen} onSaved={() => setHasProduct(true)} />
      <CreateEpdDialog open={epdOpen} onOpenChange={setEpdOpen} onCreated={async (id) => { await finish(); nav(`/app/epds/${id}`); }} />
    </div>
  );
}
